import React from 'react';
import { dayLabel, fieldLabel } from '../../utils/timesheetDisplay';

/**
 * THE PRE-SUBMIT CHECK (owner, 2026-10-26: "Missing details: (list details)
 * are you sure you want to submit?"). Built from the SERVER's own lists on a
 * freshly fetched week — `required_blank` (blocks: no call/wrap/unit, the day
 * cannot be costed), `gaps` (warns: meal times, Day type, Day #), the status
 * (conflicting, rate missing) — so the client decides nothing about readiness.
 *
 * `scope` = the people Submit covers. → { blocked, gaps, conflicts,
 * rateMissing, ready, empty }: `empty` = nothing to warn about.
 */
export function submitReport(scope) {
  const blocked = [];
  const conflicts = [];
  const rateMissing = [];
  const gapMap = new Map(); // "date|fields" → { date, fields, names }
  let ready = 0;
  for (const p of scope) {
    if (p.status === 'submitted') continue;
    const blockDays = (p.days || []).filter((d) => (d.required_blank || []).length > 0).map((d) => dayLabel(d.date));
    if (blockDays.length) blocked.push({ name: p.name, days: blockDays });
    if (p.status === 'conflicting') conflicts.push(p.name);
    if (p.status === 'rate_missing') rateMissing.push(p.name);
    if (p.status === 'reconciled') ready += 1;
    for (const d of p.days || []) {
      const g = d.gaps || [];
      if (!g.length) continue;
      const key = `${d.date}|${g.join(',')}`;
      if (!gapMap.has(key)) gapMap.set(key, { date: d.date, fields: g, names: [] });
      gapMap.get(key).names.push(p.name);
    }
  }
  const gaps = [...gapMap.values()].sort((a, b) => a.date.localeCompare(b.date));
  return { blocked, gaps, conflicts, rateMissing, ready, empty: !blocked.length && !gaps.length && !conflicts.length && !rateMissing.length };
}

const who = (names) => (names.length === 1 ? names[0] : `${names.length} people`);

/** The warning's body (inside a ConfirmDialog). */
export function SubmitReportBody({ report }) {
  const li = 'text-gray-700 dark:text-gray-300';
  return (
    <div className="space-y-3 text-left text-sm" data-testid="submit-report">
      {report.blocked.length > 0 && (
        <div>
          <p className="font-medium text-red-700 dark:text-red-400">Can’t be submitted — no call or wrap ({report.blocked.length}):</p>
          <ul className="mt-1 space-y-0.5">
            {report.blocked.map((b) => <li key={b.name} className={li}>{b.name} <span className="text-gray-500 dark:text-gray-400">({b.days.join(', ')})</span></li>)}
          </ul>
        </div>
      )}
      {report.gaps.length > 0 && (
        <div>
          <p className="font-medium text-amber-700 dark:text-amber-400">Will submit with missing details:</p>
          <ul className="mt-1 space-y-0.5">
            {report.gaps.map((g) => (
              <li key={`${g.date}|${g.fields.join(',')}`} className={li}>
                {dayLabel(g.date)}: {g.fields.map(fieldLabel).join(', ').toLowerCase()} — {who(g.names)}
                {g.fields.some((f) => f === 'meal_start' || f === 'meal_end') && <span className="text-gray-500 dark:text-gray-400"> (meal penalty not checked)</span>}
              </li>
            ))}
          </ul>
        </div>
      )}
      {report.conflicts.length > 0 && (
        <p className="font-medium text-amber-700 dark:text-amber-400"><span aria-hidden="true">▲ </span>{report.conflicts.length} {report.conflicts.length === 1 ? 'person has conflicts' : 'people have conflicts'} to reconcile — left open for you.</p>
      )}
      {report.rateMissing.length > 0 && (
        <p className={li}>Rate missing (left open): {report.rateMissing.join(', ')}</p>
      )}
      <p className="text-gray-600 dark:text-gray-400">Crew can no longer change this week once submitted. You still can.</p>
    </div>
  );
}
